import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Divider, Paper, Typography } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../hooks/usePermissions";
import { REAUTH_REQUIRED, TIFOXR_AUTH_ERROR_KEY } from "../api/apiConfig";
import logo from "../assets/logo.png";
import cmsLogo from "../assets/TifoXR_CMS.jpeg";
import LocalText from "../components/common/LocalText";
import Footer from "../components/homepage/Footer";

function getMsalRedirectError() {
  const err = sessionStorage.getItem("msal_redirect_error");
  if (err) sessionStorage.removeItem("msal_redirect_error");
  return err;
}

function getAuthFailureMessage() {
  const err = sessionStorage.getItem(TIFOXR_AUTH_ERROR_KEY);
  if (err) sessionStorage.removeItem(TIFOXR_AUTH_ERROR_KEY);
  return err;
}

export default function Login() {
  const { isAuthenticated, isLoading, login, user, setActiveSpace, signInError, retrySignIn, isExchanging } = useAuth();
  const { isSystemUser, isGmsAccountant } = usePermissions();
  const navigate = useNavigate();
  // Lazy init: reads sessionStorage once on mount; direct calls would re-clear it on every re-render.
  const [msalRedirectError] = useState(getMsalRedirectError);
  const [authFailureMessage] = useState(getAuthFailureMessage);

  useEffect(() => {
    // Bail if signInError or isExchanging is set to avoid redirect ping-pong with ProtectedRoute.
    if (!isAuthenticated || isLoading || isExchanging || !user?.id || signInError) return;

    const spaces = user.spaces ?? [];
    if (isGmsAccountant && spaces.length === 0) {
      navigate('/', { replace: true });
    } else if (isSystemUser && spaces.length > 0) {
      navigate('/select-space', { replace: true });
    } else if (isSystemUser) {
      navigate('/admin', { replace: true });
    } else if (spaces.length > 1) {
      navigate('/select-space', { replace: true });
    } else if (spaces.length === 1) {
      setActiveSpace(spaces[0]);
      navigate('/', { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isLoading, isExchanging, user?.id, signInError]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isAuthenticated && !signInError && !isLoading && !isExchanging) return null;

  return (
    <Box
      sx={{
        minHeight: ["100vh", "100dvh"],
        bgcolor: "#0a326d",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          width: { xs: "90%", sm: 380 },
          maxWidth: 380,
          bgcolor: "#ffffff",
        }}
      >
        {/* Logo section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 2.5,
            mb: 1,
          }}
        >
          <Box component="img" src={logo} alt="TifoXR Logo" sx={{ height: 64, objectFit: "contain" }} />
          <Divider orientation="vertical" flexItem sx={{ borderColor: "#cbd8ea", borderRightWidth: 2, borderRadius: 1 }} />
          <Box component="img" src={cmsLogo} alt="TifoXR CMS Logo" sx={{ height: 64, objectFit: "contain" }} />
        </Box>

        <Typography variant="h5" fontWeight="bold" color="#0a326d">
          <LocalText>CMS Sign In</LocalText>
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          <LocalText>Sign in to access the TifoXR Content Management System.</LocalText>
        </Typography>

        {msalRedirectError && (
          <Alert severity="error" sx={{ width: "100%", fontSize: "0.75rem" }}>
            {msalRedirectError}
          </Alert>
        )}

        {authFailureMessage && (
          <Alert severity="error" sx={{ width: "100%", fontSize: "0.75rem" }}>
            {authFailureMessage}
          </Alert>
        )}

        {signInError && (
          <Alert severity="error" sx={{ width: "100%", fontSize: "0.75rem" }}>
            {signInError.message}
          </Alert>
        )}

        <Divider sx={{ width: "100%" }} />

        {!signInError && (isLoading || isExchanging) ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 1 }}>
            <CircularProgress size={28} sx={{ color: "#0a326d" }} />
            <Typography variant="body2" color="text.secondary">
              <LocalText>Signing you in…</LocalText>
            </Typography>
          </Box>
        ) : (
          <Button
            variant="contained"
            fullWidth
            onClick={signInError ? retrySignIn : login}
            sx={{
              bgcolor: "#0a326d",
              "&:hover": { bgcolor: "#08264e" },
              textTransform: "none",
              fontWeight: "bold",
              py: 1.5,
              borderRadius: 2,
            }}
          >
            <LocalText>
              {signInError ? (signInError.kind === REAUTH_REQUIRED ? "Sign in again" : "Try Again") : "Sign in"}
            </LocalText>
          </Button>
        )}

        <Typography variant="caption" color="text.disabled" textAlign="center">
          <LocalText>Access is restricted to authorized personnel only.</LocalText>
        </Typography>
      </Paper>

      <Footer dark />
    </Box>
  );
}
